import React, { useMemo } from 'react';

/** One column: where its cell comes from and what its header says. */
export interface DataTableColumn<T> {
  /** Stable identity of the column; also the React key of every cell in it. */
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  /** Extra classes for this column's header and cells (width, alignment). */
  className?: string;
}

export interface DataTableProps<T> {
  columns: Array<DataTableColumn<T>>;
  rows: T[];
  getRowKey: (row: T) => string;
  /** Makes every row activatable, by click and by keyboard. */
  onRowClick?: (row: T) => void;
  /** Shown in place of the body when there is nothing to list. */
  emptyText?: string;
  /** Groups the rows under headers; without it the table is flat. */
  groupBy?: (row: T) => string;
  /** Renders one group header row. Required as soon as `groupBy` is given. */
  renderGroupHeader?: (groupKey: string, rows: T[]) => React.ReactNode;
  /**
   * The groups to render, in this order. Without it the groups are the ones
   * the rows produce, in first-seen order.
   *
   * The console's Islands tab needs more than the rows can say: an
   * organization that owns no island has no row to be derived from, and it is
   * exactly the one a superadmin may delete — so it has to appear, with its
   * header and an empty body.
   */
  groupKeys?: string[];
  className?: string;
}

/** Rows bucketed by their group key, in the order the groups should appear. */
function groupRows<T>(
  rows: T[],
  groupBy: (row: T) => string,
  groupKeys: string[] | undefined
): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();

  // Seed the declared groups first, so their order is honoured and the empty
  // ones survive having no rows to put in them.
  for (const key of groupKeys ?? []) {
    groups.set(key, []);
  }

  for (const row of rows) {
    const key = groupBy(row);
    const bucket = groups.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      groups.set(key, [row]);
    }
  }

  return [...groups.entries()];
}

const HEADER_CELL = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-400';
const BODY_CELL = 'px-3 py-3 text-sm text-gray-200 align-middle';

/**
 * The console's one table (§8). It is deliberately small: columns in, rows out,
 * optionally grouped under a header the caller renders.
 *
 * It holds no state of its own — no sorting, no selection, no pagination. The
 * console has none of those (§7 caps lists at 500 rows and narrows them with a
 * search term instead), and a table that owned state would have to be kept in
 * step with the tab that owns the data.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  onRowClick,
  emptyText = 'Nothing to show',
  groupBy,
  renderGroupHeader,
  groupKeys,
  className = ''
}: DataTableProps<T>) {
  const groups = useMemo(
    () => (groupBy ? groupRows(rows, groupBy, groupKeys) : null),
    [rows, groupBy, groupKeys]
  );

  const isEmpty = groups ? groups.length === 0 : rows.length === 0;

  const renderRow = (row: T) => {
    const clickable = onRowClick !== undefined;

    return (
      <tr
        key={getRowKey(row)}
        // A row is only focusable when activating it does something; otherwise
        // it would be a tab stop that answers to nothing. It keeps its `row`
        // role: calling it a button would cost a screen reader the column
        // structure that is the point of putting the data in a table.
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => onRowClick(row) : undefined}
        onKeyDown={
          clickable
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRowClick(row);
                }
              }
            : undefined
        }
        className={`border-t border-game-medium/20 ${
          clickable
            ? 'cursor-pointer hover:bg-game-medium/30 focus:bg-game-medium/30 focus:outline-none'
            : ''
        }`}
      >
        {columns.map(column => (
          <td key={column.key} className={`${BODY_CELL} ${column.className ?? ''}`.trim()}>
            {column.render(row)}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={`overflow-x-auto rounded-lg border border-game-medium/30 ${className}`.trim()}>
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-game-medium/20">
            {columns.map(column => (
              <th
                key={column.key}
                scope="col"
                className={`${HEADER_CELL} ${column.className ?? ''}`.trim()}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-400 italic">
                {emptyText}
              </td>
            </tr>
          ) : groups ? (
            groups.map(([groupKey, groupRowsInGroup]) => (
              <React.Fragment key={groupKey}>
                <tr className="bg-game-dark/60">
                  <td colSpan={columns.length} className="px-3 py-2">
                    {renderGroupHeader ? renderGroupHeader(groupKey, groupRowsInGroup) : groupKey}
                  </td>
                </tr>
                {groupRowsInGroup.map(renderRow)}
              </React.Fragment>
            ))
          ) : (
            rows.map(renderRow)
          )}
        </tbody>
      </table>
    </div>
  );
}
